import React, { useEffect, useRef } from 'react';
import { useResources } from '@platform/core/src/context/ResourceContext';
import { useAlert } from '@platform/core/src/context/AlertContext';
import { useUI } from '@platform/core/src/context/UIContext';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useConfig } from '@platform/core/src/context/ConfigContext';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { updateWorkoutSession } from '../../services/workoutSessionApi';

const WorkoutAlerts = () => {
  const { resources, refreshResources } = useResources();
  const { setAlert, alert } = useAlert();
  const { openResourceTab } = useUI();
  const {
    activeWidgets,
    openWidget,
    closeWidget,
    teamName,
    sessionId,
    sessionContext,
    initializeSession
  } = useSession();
  const { chatTeamConfigs } = useConfig();
  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    const activeSession = resources.find(
      r => (r.type === 'workout_session' || r.resource_type === 'workout_session') &&
           r.status === 'IN_PROGRESS'
    );

    const isSuppressedByTeam = chatTeamConfigs?.[teamName]?.suppressGlobalAlerts;
    
    // Check if user is currently "in" this session via chat context or session ID
    const isUserInThisWorkoutChat = activeSession?.chat_session_id && activeSession.chat_session_id === sessionId;
    const isSessionInContext = activeSession && sessionContext?.some(
        ctx => (ctx.type === 'workout_session' || ctx.resource_type === 'workout_session') &&
               String(ctx.id) === String(activeSession.id)
    );

    const shouldShowAlert = activeSession &&
                            !isSuppressedByTeam &&
                            !isUserInThisWorkoutChat &&
                            !isSessionInContext;

    if (shouldShowAlert) {
      const alertId = `session-${activeSession.id}`;
      const displayMessage = activeSession.name
        ? `In Progress: ${activeSession.name}`
        : "Workout In Progress";

      if (!alert || alert.id !== alertId || alert.message !== displayMessage) {
        setAlert({
          id: alertId,
          message: displayMessage,
          severity: 'info',
          icon: FitnessCenterIcon,
          actions: [
            {
                label: "Done",
                variant: "outlined",
                color: "inherit",
                onClick: async () => {
                    try {
                        // Since updateWorkoutSession uses PUT, we must provide the full object
                        // (or at least all required fields like 'name') to avoid 422 errors.
                        const payload = {
                            name: activeSession.name,
                            status: 'COMPLETED',
                            end_time: new Date().toISOString(),
                            start_time: activeSession.start_time,
                            user_notes: activeSession.user_notes,
                            location_id: activeSession.location_id,
                            workout_plan_id: activeSession.workout_plan_id,
                            chat_session_id: activeSession.chat_session_id,
                            date_id: activeSession.date_id,
                        };

                        await updateWorkoutSession(activeSession.id, payload);
                        refreshResources();
                    } catch (error) {
                        console.error("Failed to complete workout session from alert:", error);
                    }
                }
            },
            {
                label: "Resume",
                variant: "contained",
                onClick: async () => {
                  openResourceTab(activeSession);
                  
                  const newSessionOptions = {
                      team_name: 'active_workout_team',
                      context: [{ id: activeSession.id, type: 'workout_session' }]
                  };

                  if (activeSession.chat_session_id) {
                      try {
                          await initializeSession(activeSession.chat_session_id, newSessionOptions);
                      } catch (resumeError) {
                          // If the old chat session is gone (404), start a new one and link it
                          if (resumeError.status === 404 || resumeError.message?.includes('not found')) {
                              const newChatSessionId = await initializeSession(null, newSessionOptions);
                              if (newChatSessionId) {
                                  // We need to send the full payload here as well for the PUT request
                                  const payload = {
                                      name: activeSession.name,
                                      status: activeSession.status,
                                      start_time: activeSession.start_time,
                                      end_time: activeSession.end_time,
                                      user_notes: activeSession.user_notes,
                                      location_id: activeSession.location_id,
                                      workout_plan_id: activeSession.workout_plan_id,
                                      chat_session_id: newChatSessionId,
                                      date_id: activeSession.date_id,
                                  };
                                  await updateWorkoutSession(activeSession.id, payload);
                              }
                          } else {
                              throw resumeError;
                          }
                      }
                  } else {
                      const newChatSessionId = await initializeSession(null, newSessionOptions);
                      if (newChatSessionId) {
                          const payload = {
                              name: activeSession.name,
                              status: activeSession.status,
                              start_time: activeSession.start_time,
                              end_time: activeSession.end_time,
                              user_notes: activeSession.user_notes,
                              location_id: activeSession.location_id,
                              workout_plan_id: activeSession.workout_plan_id,
                              chat_session_id: newChatSessionId,
                              date_id: activeSession.date_id,
                          };
                          await updateWorkoutSession(activeSession.id, payload);
                      }
                  }
                }
            }
          ]
        });

        // Auto-open the widget if it's not already open and we haven't done so yet for this session
        if (!activeWidgets.includes('alertWidget') && !hasAutoOpenedRef.current) {
           if (openWidget) {
             openWidget('alertWidget');
             hasAutoOpenedRef.current = true;
           }
        }
      }
    } else {
      // If the alert is currently showing a session alert, but the condition is no longer met
      // (e.g. user clicked Resume, or status changed to COMPLETED), clear it.
      if (alert && alert.id.startsWith('session-')) {
        setAlert(null);
        hasAutoOpenedRef.current = false; // Reset auto-open flag
        
        // Optional: Auto-close widget if it was only showing this alert
        if (activeWidgets.includes('alertWidget')) {
            if (closeWidget) {
                closeWidget('alertWidget');
            }
        }
      }
    }
  }, [
    resources,
    alert,
    setAlert,
    openResourceTab,
    activeWidgets,
    openWidget,
    closeWidget,
    teamName,
    sessionId,
    sessionContext,
    chatTeamConfigs,
    initializeSession,
    refreshResources
  ]);

  return null;
};

export default WorkoutAlerts;